package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.util.AppClock
import evoshift.composeapp.generated.resources.contract_owner_partner
import com.evoapps.evoshift.ui.finance.formatMoney
import com.evoapps.evoshift.ui.finance.PayFactorTexts
import com.evoapps.evoshift.ui.finance.payFactorLines
import com.evoapps.evoshift.domain.market.payroll.MonthPayFactors
import com.evoapps.evoshift.domain.model.capabilitiesAt
import androidx.compose.ui.text.intl.Locale
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.market.payroll.PayLineKind
import com.evoapps.evoshift.domain.market.payroll.poland.PolandRates
import com.evoapps.evoshift.domain.model.AbsenceType
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.SystemContractType
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.UserPermission
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.usecase.GetOwnerUseCase
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.financial.GeneratePayrollReportUseCase
import com.evoapps.evoshift.domain.usecase.reports.GenerateWorkingTimeReportUseCase
import com.evoapps.evoshift.ui.reports.ReportHtml
import com.evoapps.evoshift.ui.reports.ReportLabels
import com.evoapps.evoshift.ui.reports.printHtml
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.months
import com.evoapps.evoshift.ui.util.labelRes
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.*
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.getString

/** Who the working-time report covers. */
enum class WorkingTimeScope { ALL_MY_WORKPLACES, WORKPLACE, EMPLOYEE }

data class ReportPrintState(val busy: Boolean = false, val error: UiText? = null)

/**
 * Builds and prints the monthly reports. Working time: managers and the owner of the workplace
 * (for "all my workplaces": every workplace where the person may print it). Payroll: financial
 * managers and the owner. The data rules enforce the same on the server.
 */
class ReportPrintViewModel(
    private val getCurrentUser: GetCurrentUserUseCase,
    private val generateWorkingTime: GenerateWorkingTimeReportUseCase,
    private val generatePayroll: GeneratePayrollReportUseCase,
    private val workplaceRepository: WorkplaceRepository,
    private val getOwner: GetOwnerUseCase
) : ViewModel() {
    private val _state = MutableStateFlow(ReportPrintState())
    val state = _state.asStateFlow()

    fun clearError() = _state.update { it.copy(error = null) }

    fun printWorkingTime(scope: WorkingTimeScope, workplaceId: String, year: Int, month: Int, userId: String? = null) = run {
        val user = getCurrentUser().first() ?: error("signed out")
        val workplaces = when (scope) {
            WorkingTimeScope.ALL_MY_WORKPLACES -> user.workplacesWhere(Gate.PRINT_WORKING_TIME)
            else -> listOf(workplaceId).filter { user.capabilitiesAt(it).shows(Gate.PRINT_WORKING_TIME) }
        }
        if (workplaces.isEmpty()) return@run UiText.Resource(Res.string.print_not_allowed)
        val sheets = generateWorkingTime(workplaces, year, month, userId.takeIf { scope == WorkingTimeScope.EMPLOYEE }).getOrThrow()
        if (sheets.isEmpty()) return@run UiText.Resource(Res.string.print_nothing)
        val labels = labels()
        printHtml("${labels.workingTimeTitle} ${month.toString().padStart(2, '0')}-$year", ReportHtml.workingTime(sheets, labels, employers(user, sheets.map { it.workplaceId })))
        null
    }

    fun printPayroll(rosterId: String, userId: String? = null) = run {
        val user = getCurrentUser().first() ?: error("signed out")
        val workplaceId = RosterIds.workplaceIdOf(rosterId)
        if (!user.capabilitiesAt(workplaceId).shows(Gate.PAYROLL)) return@run UiText.Resource(Res.string.print_not_allowed)
        val payroll = generatePayroll(rosterId).getOrThrow()
        if (payroll.employees.none { userId == null || it.userId == userId }) return@run UiText.Resource(Res.string.print_nothing)
        val workplaceName = workplaceRepository.getWorkplace(workplaceId).getOrNull()?.name.orEmpty()
        val labels = labels(PolandRates.forYear(payroll.year).year)
        val title = if (userId == null) labels.payrollTitle else labels.payslipTitle
        printHtml("$title ${payroll.month.toString().padStart(2, '0')}-${payroll.year}", ReportHtml.payroll(payroll, workplaceName, labels, userId))
        null
    }

    /**
     * SH-251: the employer of each workplace for the record's header: the owner's business name.
     * Only the owner may read it (the data rules), so a manager's printout leaves it out.
     */
    private suspend fun employers(user: User, workplaceIds: List<String>): Map<String, String> =
        workplaceIds.distinct().mapNotNull { id ->
            val workplace = workplaceRepository.getWorkplace(id).getOrNull() ?: return@mapNotNull null
            if (workplace.ownerId != user.uid) return@mapNotNull null
            getOwner(workplace.ownerId).getOrNull()?.businessName?.takeIf { it.isNotBlank() }?.let { id to it }
        }.toMap()

    /** Runs [block] busy; a returned text is shown as the error (nothing to print, not allowed). */
    private fun run(block: suspend () -> UiText?) {
        if (_state.value.busy) return
        viewModelScope.launch {
            _state.update { it.copy(busy = true, error = null) }
            val error = runCatching { block() }.fold(onSuccess = { it }, onFailure = { it.toUiText() })
            _state.update { it.copy(busy = false, error = error) }
        }
    }

    /** The workplaces where [this] person's capabilities show [gate] (an ended engagement shows nothing). */
    private fun User.workplacesWhere(gate: Gate) =
        engagements.keys.filter { capabilitiesAt(it).shows(gate) }

    /** [ratesYear]: the year whose payroll rates a payroll report uses (named in its note). */
    private suspend fun labels(ratesYear: Int = 0): ReportLabels {
        val monthNames = months.map { getString(it) }
        val days = mapOf(
            DayOfWeek.MONDAY to getString(Res.string.day_mon_short), DayOfWeek.TUESDAY to getString(Res.string.day_tue_short),
            DayOfWeek.WEDNESDAY to getString(Res.string.day_wed_short), DayOfWeek.THURSDAY to getString(Res.string.day_thu_short),
            DayOfWeek.FRIDAY to getString(Res.string.day_fri_short), DayOfWeek.SATURDAY to getString(Res.string.day_sat_short),
            DayOfWeek.SUNDAY to getString(Res.string.day_sun_short)
        )
        // SH-258: the full names, for the records that print the day of the week so.
        val fullDays = mapOf(
            DayOfWeek.MONDAY to getString(Res.string.day_monday), DayOfWeek.TUESDAY to getString(Res.string.day_tuesday),
            DayOfWeek.WEDNESDAY to getString(Res.string.day_wednesday), DayOfWeek.THURSDAY to getString(Res.string.day_thursday),
            DayOfWeek.FRIDAY to getString(Res.string.day_friday), DayOfWeek.SATURDAY to getString(Res.string.day_saturday),
            DayOfWeek.SUNDAY to getString(Res.string.day_sunday)
        )
        val roundingModes = com.evoapps.evoshift.domain.model.TimeRoundingMode.entries.associateWith {
            getString(when (it) {
                com.evoapps.evoshift.domain.model.TimeRoundingMode.NEAREST -> Res.string.report_rounding_nearest
                com.evoapps.evoshift.domain.model.TimeRoundingMode.START_UP_END_DOWN -> Res.string.report_rounding_start_up_end_down
                com.evoapps.evoshift.domain.model.TimeRoundingMode.START_DOWN_END_UP -> Res.string.report_rounding_start_down_end_up
            })
        }
        val roundingNote = getString(Res.string.report_rounding_note)
        // SH-223: every request type.
        val absences = AbsenceType.entries.associateWith { getString(it.labelRes()) }
        val contracts = mapOf(
            SystemContractType.STANDARD_EMPLOYMENT to getString(Res.string.contract_employment),
            SystemContractType.NOT_SET to getString(Res.string.contract_employment),
            SystemContractType.CIVIL_LAW_CONTRACT to getString(Res.string.contract_civil),
            SystemContractType.SPECIFIC_TASK_CONTRACT to getString(Res.string.contract_specific_task),
            SystemContractType.B2B_CONTRACTOR to getString(Res.string.contract_b2b),
            SystemContractType.OWNER_PARTNER to getString(Res.string.contract_owner_partner)
        )
        val lines = mapOf(
            PayLineKind.PENSION to getString(Res.string.line_pension), PayLineKind.DISABILITY to getString(Res.string.line_disability),
            PayLineKind.SICKNESS to getString(Res.string.line_sickness), PayLineKind.ACCIDENT to getString(Res.string.line_accident),
            PayLineKind.HEALTH to getString(Res.string.line_health), PayLineKind.INCOME_TAX to getString(Res.string.line_income_tax),
            PayLineKind.LABOUR_FUND to getString(Res.string.line_labour_fund), PayLineKind.GUARANTEE_FUND to getString(Res.string.line_guarantee_fund),
            PayLineKind.PPK to getString(Res.string.line_ppk)
        )
        // SH-57: the month's pay lines (overtime, Sunday and holiday work, the minimum wage, leave,
        // pay changes), the same as on screen.
        val payFactorTexts = PayFactorTexts.get()
        val payFactorLines = { f: MonthPayFactors, currency: String -> payFactorLines(f, currency, payFactorTexts) }
        val now = AppClock.now().toLocalDateTime(TimeZone.currentSystemDefault())
        val stamp = "${now.day.toString().padStart(2, '0')}.${(now.month.ordinal + 1).toString().padStart(2, '0')}.${now.year} " +
            "${now.hour.toString().padStart(2, '0')}:${now.minute.toString().padStart(2, '0')}"
        return ReportLabels(
            language = Locale.current.language,
            workingTimeTitle = getString(Res.string.report_working_time_title),
            payrollTitle = getString(Res.string.report_payroll_title),
            payslipTitle = getString(Res.string.report_payslip_title),
            employee = getString(Res.string.report_employee),
            workplace = getString(Res.string.report_workplace),
            role = getString(Res.string.report_role),
            month = getString(Res.string.report_month),
            contract = getString(Res.string.report_contract),
            colDate = getString(Res.string.report_col_date),
            colDay = getString(Res.string.report_col_day),
            colPlanned = getString(Res.string.report_col_planned),
            colClocked = getString(Res.string.report_col_clocked),
            colHours = getString(Res.string.report_col_hours),
            colNight = getString(Res.string.report_col_night),
            colNotes = getString(Res.string.report_col_notes),
            sunday = getString(Res.string.report_sunday),
            holiday = getString(Res.string.report_holiday),
            plannedOnly = getString(Res.string.report_planned_only),
            totalHours = getString(Res.string.report_total_hours),
            norm = getString(Res.string.report_norm),
            nightHours = getString(Res.string.report_night_hours),
            sundayHolidayHours = getString(Res.string.report_sunday_holiday_hours),
            absences = getString(Res.string.report_absences),
            signEmployee = getString(Res.string.report_sign_employee),
            signEmployer = getString(Res.string.report_sign_employer),
            generated = getString(Res.string.report_generated, stamp),
            gross = getString(Res.string.payroll_gross),
            toPay = getString(Res.string.payroll_to_pay),
            employerCost = getString(Res.string.payroll_employer_cost),
            deductedFromPay = getString(Res.string.payroll_employee_part),
            paidByEmployer = getString(Res.string.payroll_employer_part),
            bonuses = getString(Res.string.payroll_bonuses),
            deductions = getString(Res.string.payroll_deductions),
            refunds = getString(Res.string.payroll_refunds),
            hoursWorked = getString(Res.string.report_hours_worked),
            total = getString(Res.string.report_total),
            disclaimer = getString(Res.string.payroll_disclaimer, ratesYear),
            planVsActual = getString(Res.string.report_plan_vs_actual),
            plannedHours = getString(Res.string.payroll_col_planned_hours),
            employerCostPlan = getString(Res.string.payroll_employer_cost_plan),
            monthName = { monthNames[it - 1] },
            dayShort = { days.getValue(it) },
            absence = { absences.getValue(it) },
            contractName = { contracts.getValue(it) },
            line = { lines.getValue(it) },
            payFactorLines = payFactorLines,
            employer = getString(Res.string.report_employer),
            dayFull = { fullDays.getValue(it) },
            colManagerSignature = getString(Res.string.report_col_manager_signature),
            roundingNote = { o ->
                if (!o.rounds) null
                else roundingNote.replace("%1\$d", o.roundingMinutes.toString()).replace("%2\$s", roundingModes.getValue(o.roundingMode))
            }
        )
    }
}
